// Schmale Huelle um fetch. Zwei Dinge macht sie zusaetzlich:
//  1. Sie schickt das CSRF-Token aus dem Cookie bei jedem schreibenden Aufruf mit -
//     ohne das lehnt Spring Security die Anfrage ab.
//  2. Sie hebt Fehlerantworten des Backends ({"meldung": "..."}) als Ausnahme heraus,
//     damit die Oberflaeche sie an einer Stelle anzeigen kann.

export class ApiFehler extends Error {
  constructor(
    public readonly status: number,
    meldung: string,
  ) {
    super(meldung)
  }
}

function csrfToken(): string {
  const treffer = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/)
  return treffer ? decodeURIComponent(treffer[1]) : ''
}

async function anfrage<T>(pfad: string, optionen: RequestInit = {}): Promise<T> {
  const methode = (optionen.method ?? 'GET').toUpperCase()
  const kopfzeilen = new Headers(optionen.headers)

  if (methode !== 'GET' && methode !== 'HEAD') {
    kopfzeilen.set('X-XSRF-TOKEN', csrfToken())
  }

  const antwort = await fetch(pfad, {
    ...optionen,
    method: methode,
    headers: kopfzeilen,
    credentials: 'same-origin',
  })

  if (antwort.status === 204) {
    return undefined as T
  }

  const inhaltstyp = antwort.headers.get('content-type') ?? ''
  const koerper = inhaltstyp.includes('application/json') ? await antwort.json() : await antwort.text()

  if (!antwort.ok) {
    const meldung =
      typeof koerper === 'object' && koerper !== null && 'meldung' in koerper
        ? String((koerper as { meldung: unknown }).meldung)
        : `Fehler ${antwort.status}`
    throw new ApiFehler(antwort.status, meldung)
  }

  return koerper as T
}

export const api = {
  get: <T>(pfad: string) => anfrage<T>(pfad),

  post: <T>(pfad: string, koerper?: unknown) =>
    anfrage<T>(pfad, {
      method: 'POST',
      headers: koerper === undefined ? {} : { 'Content-Type': 'application/json' },
      body: koerper === undefined ? undefined : JSON.stringify(koerper),
    }),

  put: <T>(pfad: string, koerper: unknown) =>
    anfrage<T>(pfad, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(koerper),
    }),

  patch: <T>(pfad: string, koerper: unknown) =>
    anfrage<T>(pfad, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(koerper),
    }),

  loeschen: <T>(pfad: string) => anfrage<T>(pfad, { method: 'DELETE' }),

  /** Bild-Upload als multipart/form-data; den Content-Type setzt der Browser selbst. */
  bild: <T>(pfad: string, bild: Blob, dateiname: string) => {
    const formular = new FormData()
    formular.append('bild', bild, dateiname)
    return anfrage<T>(pfad, { method: 'POST', body: formular })
  },
}
